import {
  ClusterOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Popover, Space, Tag, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DeleteTopPlanApi from "../../api/DeleteTopPlanApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { permissionAbility } from "../../helpers/permissionHelpers";
import { getProgressColor } from "../../utils/progressColors";
import EditSplitPlanModal from "../modal/edit/EditSplitPlanModal";
import HardDeleteModal from "../modal/HardDeleteModal";
import SplitPlanInfoModal from "../modal/info/SplitPlanInfoModal";
import SplitPlanModal from "../modal/SplitPlanModal";
import SplitPlanSpace from "./SplitPlanSpace";

function SplitPlanElement(props) {
  const [activeKeys, setActiveKeys] = useState([]);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState();
  const [splitModal, setSplitModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [canCollapse, setCanCollapse] = useState(true);
  const [collapse, setCollapse] = useState(true);
  const [splitedData, setSplitedData] = useState();
  const [splitCount, setSplitCount] = useState(0);
  const [targetAchived, setTargetAchieved] = useState(0);
  const [performance, setPerformance] = useState(0);

  useEffect(() => {
    if (!props.data?.hasChild) {
      setCanCollapse(false);
    }
  }, [props.data]);

  useEffect(() => {
    if (targetAchived) {
      setPerformance(((targetAchived / props.data?.target) * 100).toFixed(2));
    }
  }, [targetAchived, props.data?.target]);

  // Update performance when props.data changes (for immediate updates)
  useEffect(() => {
    if (props.data?.performance !== undefined) {
      setPerformance(props.data.performance);
    }
  }, [props.data?.performance]);

  async function deletePlan() {
    const apiResponse = await DeleteTopPlanApi(selectedPlan?.planId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Top Plan deleted");
      props.handleDelete(selectedPlan?.planId);
      props.handleDeleteChange(props.totalChildCount - 1);
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setSelectedPlan();
  }

  function handleSplit(splitedPlan) {
    setSplitedData(splitedPlan);
    setSplitCount(splitCount + 1);
  }
  function handleExpand() {
    setActiveKeys([props.data?.planId]);
  }

  function handleDeleteChange(totalChildCount) {
    totalChildCount === 0 && setCanCollapse(false);
  }
  return (
    <>
      <>
        <div
          className="flex items-center rounded-md cursor-pointer"
          onClick={() => {
            setSelectedPlan(props.data);
            setInfoModal(true);
          }}
        >
          <div className="flex justify-center items-center w-8">
            {canCollapse ? (
              collapse ? (
                <PlusSquareOutlined
                  className="cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleExpand();
                    setCollapse(!collapse);
                  }}
                />
              ) : (
                <MinusSquareOutlined
                  className="cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    setCollapse(!collapse);
                  }}
                />
              )
            ) : (
              ""
            )}
          </div>
          <div className="grid grid-cols-6 gap-5 border w-full ml-2 p-3 ">
            <div className="col-span-2">{props.data?.title}</div>
            <div>
              <Tag color={""}>{props.data?.planLevel}</Tag>
            </div>
            <div className="flex items-center">
              {props.data?.assignedTo?.imageUrl ? (
                <Avatar
                  className="cursor-pointer"
                  src={props.data?.assignedTo?.imageUrl}
                />
              ) : (
                <Avatar>
                  {props.data?.assignedTo?.firstName ? (
                    props.data?.assignedTo?.firstName?.charAt(0).toUpperCase()
                  ) : (
                    <UserOutlined />
                  )}
                </Avatar>
              )}
              <div className="ml-2">
                <div>{props.data?.assignedTo?.firstName || "-"}</div>
                <div className="text-xs -mt-1">
                  {props.data?.assignedTo?.designationAbb}
                </div>
              </div>
            </div>
            <div>
              <Tag
                color={getProgressColor(performance || props.data?.performance || 0)}
              >
                {parseFloat(performance || props.data?.performance || 0).toFixed(2) + "%"}
              </Tag>
            </div>
            <div>
              <Space size="middle">
                {permissionAbility(userLevel, "plans", "split") &&
                  props.data?.canSplit && (
                    <Tooltip
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedPlan(props.data);
                        setSplitModal(true);
                      }}
                      title={"Split"}
                    >
                      <div className="text-blue-500 hover:text-blue-600 cursor-pointer">
                        <ClusterOutlined />
                      </div>
                    </Tooltip>
                  )}
                {permissionAbility(userLevel, "plans", "edit") && (
                  <Tooltip
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedPlan(props.data);
                      setEditModal(true);
                    }}
                    title={"Edit"}
                  >
                    <div className="text-blue-500 hover:text-blue-600 cursor-pointer">
                      <EditOutlined />
                    </div>
                  </Tooltip>
                )}
                {permissionAbility(userLevel, "plans", "delete") &&
                  (!canCollapse ? (
                    <Tooltip title={"Delete"}>
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedPlan(props.data);
                          setDeleteModal(true);
                        }}
                        className="text-red-500 hover:text-red-600 cursor-pointer"
                      >
                        <DeleteOutlined />
                      </div>
                    </Tooltip>
                  ) : (
                    <Popover
                      placement="left"
                      content={
                        <div className="text-xs w-48">
                          Can't delete this plan. This plan was splited.
                        </div>
                      }
                      trigger="hover"
                    >
                      <div className="text-gray-500">
                        <DeleteOutlined />
                      </div>
                    </Popover>
                  ))}
              </Space>
            </div>
          </div>
        </div>
        <div className={"ml-5 " + (collapse ? "hidden" : "block")}>
          <SplitPlanSpace
            planId={props.data?.planId}
            activeKeys={activeKeys}
            splitedData={splitedData}
            collapse={collapse}
            handleDeleteChange={(totalChildCount) =>
              handleDeleteChange(totalChildCount)
            }
            splitCount={splitCount}
            onParentRefresh={props.onParentRefresh}
          />
        </div>
      </>
      <SplitPlanModal
        visible={splitModal}
        plan={selectedPlan}
        handleCancel={() => {
          setSelectedPlan();
          setSplitModal(false);
        }}
        handleOk={(splitedPlan) => {
          setCanCollapse(true);
          handleSplit(splitedPlan);
          setCollapse(false);
        }}
      />
      <EditSplitPlanModal
        visible={editModal}
        handleCancel={() => {
          setSelectedPlan();
          setEditModal(false);
        }}
        plan={selectedPlan}
        handleOk={(data) => props.handleEdit(data)}
      />
      <HardDeleteModal
        visible={deleteModal}
        handleCancel={() => setDeleteModal(false)}
        handleOk={() => {
          deletePlan();
        }}
        text={`Are you sure want to delete this plan?`}
        title={`Delete plan`}
      />
      <SplitPlanInfoModal
        visible={infoModal}
        plan={selectedPlan}
        handleCancel={() => {
          setInfoModal(false);
          setSelectedPlan();
        }}
        updateTarget={(target) => {
          setTargetAchieved(target);
          // Trigger immediate re-render by updating the plan data
          if (props.onTargetUpdate) {
            props.onTargetUpdate(selectedPlan?.planId, target);
          }
        }}
      />
    </>
  );
}

export default SplitPlanElement;
